/**
 * Export Dialog Component
 * Provides UI for exporting stage layouts in multiple formats (PDF / PNG /
 * JSON). Both PDF and PNG support a 2D or 3D source view; 3D captures come
 * from the live Three.js scene at a user-chosen camera angle.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Tabs from '@radix-ui/react-tabs';
import { X, FileDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { StageLayout } from '@/stage/types';
import { useEditorStore } from '@/core/stores/editor-store';
import {
  exportCanvasPng,
  generateThumbnail,
  blobToDataUrl,
  downloadBlob,
} from './png-export';
import {
  exportLayoutJson,
  importLayoutJson,
} from './json-export';
import { generatePdfBriefing, downloadPdf } from './pdf-export';
import {
  capture3DScene,
  capture3DDataUrl,
  hasScene,
  saveCamera,
  restoreCamera,
  type SceneAngle,
} from '@/scene/scene-capture';

interface ExportDialogProps {
  layout: StageLayout;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportLayout?: (layout: StageLayout) => void;
}

type SourceView = '2d' | '3d';

const DEFAULT_ANGLE: SceneAngle = {
  yaw: Math.PI * 0.25,   // 45°, a natural hero angle
  pitch: Math.PI * 0.28, // ~50° above horizon
  distance: 30,
};

export const ExportDialog: React.FC<ExportDialogProps> = ({
  layout,
  open,
  onOpenChange,
  onImportLayout,
}) => {
  const [activeTab, setActiveTab] = useState('pdf');
  const [isGenerating, setIsGenerating] = useState(false);
  const [pdfMatchName, setPdfMatchName] = useState('');
  const [pdfPageSize, setPdfPageSize] = useState<'A4' | 'LETTER'>('A4');
  const [pngScale, setPngScale] = useState<1 | 2 | 4>(2);
  const [includeGrid, setIncludeGrid] = useState(true);
  const [diagramPreview, setDiagramPreview] = useState<string | null>(null);

  // 2D vs 3D source view for both PDF and PNG exports
  const [sourceView, setSourceView] = useState<SourceView>('3d');
  const [angle, setAngle] = useState<SceneAngle>(DEFAULT_ANGLE);
  const [scene3DPreview, setScene3DPreview] = useState<string | null>(null);
  const [tiltSwingers, setTiltSwingers] = useState(true);

  const viewMode = useEditorStore((s) => s.viewMode);
  const setViewMode = useEditorStore((s) => s.setViewMode);
  const setShowSwingersTilted = useEditorStore((s) => s.setShowSwingersTilted);

  // While the export dialog is open in 3D mode, push the user's "tilt
  // swingers" preference into the editor store so the live scene reflects
  // it (live preview + capture). Always reset on close so the editor view
  // returns to normal when the dialog closes.
  useEffect(() => {
    if (open && sourceView === '3d') {
      setShowSwingersTilted(tiltSwingers);
    } else {
      setShowSwingersTilted(false);
    }
    return () => setShowSwingersTilted(false);
  }, [open, sourceView, tiltSwingers, setShowSwingersTilted]);

  const sceneSize = useMemo(
    () => ({ w: layout.canvas.width_m, h: layout.canvas.height_m }),
    [layout.canvas.width_m, layout.canvas.height_m],
  );

  // When 3D is chosen, make sure the 3D scene is visible so capture works.
  useEffect(() => {
    if (!open || sourceView !== '3d') return;
    if (viewMode !== '3d') setViewMode('3d');
  }, [open, sourceView, viewMode, setViewMode]);

  // Snapshot / restore camera when entering / leaving 3D capture mode.
  useEffect(() => {
    if (!open || sourceView !== '3d') return;
    saveCamera();
    return () => {
      restoreCamera();
    };
  }, [open, sourceView]);

  // Live-preview: rerender when the angle sliders move or when the
  // tilt-swingers toggle changes (so the preview matches what the export
  // will capture).
  useEffect(() => {
    if (!open || sourceView !== '3d') return;
    if (!hasScene()) return;
    const t = setTimeout(() => {
      const url = capture3DDataUrl(angle, sceneSize);
      if (url) setScene3DPreview(url);
    }, 80); // debounce rapid slider drags + give useFrame a tick to apply tilt
    return () => clearTimeout(t);
  }, [open, sourceView, angle, sceneSize, tiltSwingers]);

  const handleGeneratePdf = useCallback(async () => {
    try {
      setIsGenerating(true);

      let pngBlob: Blob;
      if (sourceView === '3d') {
        if (!hasScene()) {
          toast.error('Switch to the 3D view, then try again');
          return;
        }
        pngBlob = await capture3DScene(angle, sceneSize);
      } else {
        pngBlob = await exportCanvasPng(layout, {
          width: 1200,
          height: 900,
          scale: 1,
          includeGrid: true,
        });
      }

      const diagramUrl = await blobToDataUrl(pngBlob);
      setDiagramPreview(diagramUrl);

      const blueprintBlob = sourceView === '2d'
        ? pngBlob
        : await exportCanvasPng(layout, {
            width: 1200,
            height: 900,
            scale: 1,
            includeGrid: true,
          });
      const blueprintUrl = await blobToDataUrl(blueprintBlob);

      const pdfBlob = await generatePdfBriefing(layout, diagramUrl, {
        pageSize: pdfPageSize,
        matchName: pdfMatchName,
        sourceView,
        blueprintImageDataUrl: blueprintUrl,
      });

      const filename = `${layout.metadata.name || 'stage'}-briefing.pdf`;
      downloadPdf(pdfBlob, filename);

      toast.success('PDF briefing sheet generated and downloaded');
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      toast.error(
        error instanceof Error ? error.message : 'Failed to generate PDF',
      );
    } finally {
      setIsGenerating(false);
    }
  }, [layout, pdfMatchName, pdfPageSize, sourceView, angle, sceneSize]);

  const handleExportPng = useCallback(async () => {
    try {
      setIsGenerating(true);

      let pngBlob: Blob;
      let filename = `${layout.metadata.name || 'stage'}-diagram.png`;

      if (sourceView === '3d') {
        if (!hasScene()) {
          toast.error('Switch to the 3D view, then try again');
          return;
        }
        pngBlob = await capture3DScene(angle, sceneSize);
        filename = `${layout.metadata.name || 'stage'}-3d.png`;
      } else {
        pngBlob = await exportCanvasPng(layout, {
          width: 1920,
          height: 1440,
          scale: pngScale,
          includeGrid,
        });
      }

      downloadBlob(pngBlob, filename);
      toast.success('PNG diagram exported and downloaded');
    } catch (error) {
      console.error('Failed to export PNG:', error);
      toast.error(
        error instanceof Error ? error.message : 'Failed to export PNG',
      );
    } finally {
      setIsGenerating(false);
    }
  }, [layout, pngScale, includeGrid, sourceView, angle, sceneSize]);

  const handleExportJson = useCallback(() => {
    try {
      const filename = `${layout.metadata.name || 'stage'}-layout.json`;
      exportLayoutJson(layout, filename);
      toast.success('JSON layout exported and downloaded');
    } catch (error) {
      console.error('Failed to export JSON:', error);
      toast.error('Failed to export JSON layout');
    }
  }, [layout]);

  const handleImportJson = useCallback(async () => {
    try {
      const importedLayout = await importLayoutJson();
      if (importedLayout) {
        onImportLayout?.(importedLayout);
        onOpenChange(false);
        toast.success('Layout imported successfully');
      }
    } catch (error) {
      console.error('Failed to import JSON:', error);
      toast.error('Failed to import JSON layout');
    }
  }, [onImportLayout, onOpenChange]);

  const handleGenerateThumbnail = useCallback(async () => {
    try {
      setIsGenerating(true);
      const thumbnailBlob = await generateThumbnail(layout);
      const filename = `${layout.metadata.name || 'stage'}-thumbnail.png`;
      downloadBlob(thumbnailBlob, filename);
      toast.success('Thumbnail generated and downloaded');
    } catch (error) {
      console.error('Failed to generate thumbnail:', error);
      toast.error('Failed to generate thumbnail');
    } finally {
      setIsGenerating(false);
    }
  }, [layout]);

  // Shared UI: source-view toggle used by both PDF and PNG tabs
  const SourceViewToggle = (
    <div>
      <label className="block text-sm font-medium text-gray-900 mb-2">
        View
      </label>
      <div className="flex gap-2">
        {(['2d', '3d'] as const).map((v) => (
          <button
            key={v}
            onClick={() => setSourceView(v)}
            className={`flex-1 px-3 py-2 text-sm rounded border ${
              sourceView === v
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {v === '2d' ? 'Top-down (2D)' : 'Perspective (3D)'}
          </button>
        ))}
      </div>
      {sourceView === '3d' && !hasScene() && (
        <p className="text-xs text-amber-700 mt-2">
          The 3D scene needs to be mounted. Switching to the 3D view now…
        </p>
      )}
    </div>
  );

  // Shared UI: 3D camera-angle sliders + live preview
  const AngleControls = sourceView === '3d' && (
    <div className="space-y-4">
      <div>
        <label className="flex justify-between text-sm text-gray-900 mb-1">
          <span>Yaw (rotation)</span>
          <span className="font-mono text-xs text-gray-500">
            {Math.round((angle.yaw * 180) / Math.PI)}°
          </span>
        </label>
        <input
          type="range"
          min={0}
          max={360}
          value={(angle.yaw * 180) / Math.PI}
          onChange={(e) =>
            setAngle({ ...angle, yaw: (Number(e.target.value) * Math.PI) / 180 })
          }
          className="w-full"
        />
      </div>
      <div>
        <label className="flex justify-between text-sm text-gray-900 mb-1">
          <span>Pitch (elevation)</span>
          <span className="font-mono text-xs text-gray-500">
            {Math.round((angle.pitch * 180) / Math.PI)}°
          </span>
        </label>
        <input
          type="range"
          min={5}
          max={89}
          value={(angle.pitch * 180) / Math.PI}
          onChange={(e) =>
            setAngle({ ...angle, pitch: (Number(e.target.value) * Math.PI) / 180 })
          }
          className="w-full"
        />
      </div>
      <div>
        <label className="flex justify-between text-sm text-gray-900 mb-1">
          <span>Distance</span>
          <span className="font-mono text-xs text-gray-500">
            {angle.distance.toFixed(1)} m
          </span>
        </label>
        <input
          type="range"
          min={Math.max(5, Math.max(sceneSize.w, sceneSize.h) * 0.4)}
          max={Math.max(sceneSize.w, sceneSize.h) * 3}
          step={0.5}
          value={angle.distance}
          onChange={(e) => setAngle({ ...angle, distance: Number(e.target.value) })}
          className="w-full"
        />
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => setAngle(DEFAULT_ANGLE)}
          className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
        >
          Reset
        </button>
        <button
          onClick={() =>
            setAngle({ yaw: 0, pitch: Math.PI * 0.47, distance: Math.max(sceneSize.w, sceneSize.h) })
          }
          className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
        >
          Top-down
        </button>
        <button
          onClick={() => setAngle({ yaw: 0, pitch: 0.12, distance: Math.max(sceneSize.w, sceneSize.h) * 1.2 })}
          className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
        >
          Eye-level
        </button>
      </div>

      {/* Freeze swingers at their max swing angle for the capture so the
          briefing reader can tell paper-swinger targets from stationary
          ones at a glance. */}
      <label className="flex items-center gap-2 pt-1">
        <input
          type="checkbox"
          checked={tiltSwingers}
          onChange={(e) => setTiltSwingers(e.target.checked)}
          className="rounded border-gray-300"
        />
        <span className="text-sm text-gray-900">
          Show swingers tilted (so motion is obvious in the export)
        </span>
      </label>

      {scene3DPreview && (
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Preview
          </label>
          <img
            src={scene3DPreview}
            alt="3D preview"
            className="w-full border border-gray-200 rounded-md max-h-72 object-contain bg-gray-900"
          />
        </div>
      )}
    </div>
  );

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-2xl max-h-[90vh] overflow-y-auto translate-x-[-50%] translate-y-[-50%] bg-white rounded-lg shadow-lg p-0">
          <div className="flex items-center justify-between border-b border-gray-200 p-6">
            <Dialog.Title className="text-lg font-semibold text-gray-900">
              Export Stage Layout
            </Dialog.Title>
            <Dialog.Close className="inline-flex items-center justify-center rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700">
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </Dialog.Close>
          </div>

          <Tabs.Root value={activeTab} onValueChange={setActiveTab} className="w-full">
            <Tabs.List className="flex border-b border-gray-200 px-6">
              <Tabs.Trigger
                value="pdf"
                className="py-3 px-4 text-sm font-medium text-gray-600 border-b-2 border-transparent hover:text-gray-900 data-[state=active]:border-blue-500 data-[state=active]:text-blue-600"
              >
                PDF Briefing
              </Tabs.Trigger>
              <Tabs.Trigger
                value="png"
                className="py-3 px-4 text-sm font-medium text-gray-600 border-b-2 border-transparent hover:text-gray-900 data-[state=active]:border-blue-500 data-[state=active]:text-blue-600"
              >
                PNG Diagram
              </Tabs.Trigger>
              <Tabs.Trigger
                value="json"
                className="py-3 px-4 text-sm font-medium text-gray-600 border-b-2 border-transparent hover:text-gray-900 data-[state=active]:border-blue-500 data-[state=active]:text-blue-600"
              >
                JSON Layout
              </Tabs.Trigger>
            </Tabs.List>

            {/* PDF Tab */}
            <Tabs.Content value="pdf" className="space-y-6 p-6">
              {SourceViewToggle}
              {AngleControls}

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Match Name (Optional)
                </label>
                <input
                  type="text"
                  value={pdfMatchName}
                  onChange={(e) => setPdfMatchName(e.target.value)}
                  placeholder="e.g., Spring Competition 2024"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Page Size
                </label>
                <div className="flex gap-4">
                  {(['A4', 'LETTER'] as const).map((size) => (
                    <label key={size} className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="pageSize"
                        value={size}
                        checked={pdfPageSize === size}
                        onChange={(e) => setPdfPageSize(e.target.value as 'A4' | 'LETTER')}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">{size}</span>
                    </label>
                  ))}
                </div>
              </div>

              {sourceView === '2d' && diagramPreview && (
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Diagram Preview
                  </label>
                  <img
                    src={diagramPreview}
                    alt="Stage diagram preview"
                    className="w-full border border-gray-200 rounded-md max-h-64 object-contain"
                  />
                </div>
              )}

              <button
                onClick={handleGeneratePdf}
                disabled={isGenerating}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {isGenerating ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</>
                ) : (
                  <><FileDown className="h-4 w-4" /> Generate PDF</>
                )}
              </button>
            </Tabs.Content>

            {/* PNG Tab */}
            <Tabs.Content value="png" className="space-y-6 p-6">
              {SourceViewToggle}
              {AngleControls}

              {sourceView === '2d' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-3">
                      Resolution Scale
                    </label>
                    <div className="space-y-2">
                      {([1, 2, 4] as const).map((scale) => (
                        <label
                          key={scale}
                          className="flex items-center gap-3 p-2 border border-gray-200 rounded-md cursor-pointer hover:bg-gray-50"
                        >
                          <input
                            type="radio"
                            name="scale"
                            value={scale}
                            checked={pngScale === scale}
                            onChange={(e) =>
                              setPngScale(Number(e.target.value) as 1 | 2 | 4)
                            }
                            className="rounded border-gray-300"
                          />
                          <div>
                            <span className="text-sm font-medium text-gray-900">
                              {scale}x Resolution
                            </span>
                            <span className="text-xs text-gray-500 block">
                              {1920 * scale} × {1440 * scale}px
                            </span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={includeGrid}
                      onChange={(e) => setIncludeGrid(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-900">Include grid</span>
                  </label>
                </>
              )}

              <div className="flex gap-3 flex-col sm:flex-row">
                <button
                  onClick={handleExportPng}
                  disabled={isGenerating}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {isGenerating ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Exporting…</>
                  ) : (
                    <><FileDown className="h-4 w-4" /> Export PNG</>
                  )}
                </button>

                {sourceView === '2d' && (
                  <button
                    onClick={handleGenerateThumbnail}
                    disabled={isGenerating}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 text-gray-900 text-sm font-medium rounded-md hover:bg-gray-300 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    {isGenerating ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</>
                    ) : (
                      <><FileDown className="h-4 w-4" /> Thumbnail (400×300)</>
                    )}
                  </button>
                )}
              </div>
            </Tabs.Content>

            {/* JSON Tab */}
            <Tabs.Content value="json" className="space-y-6 p-6">
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <p className="text-sm text-blue-900">
                  Export and import stage layouts as JSON files. Useful for
                  backing up, sharing, or version-controlling designs.
                </p>
              </div>

              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-900">Stage Information</h4>
                <div className="bg-gray-50 rounded-md p-3 text-xs text-gray-600 space-y-1">
                  <div><span className="font-medium">Name:</span> {layout.metadata.name}</div>
                  <div><span className="font-medium">Objects:</span> {layout.objects.length}</div>
                  <div><span className="font-medium">Walls:</span> {layout.walls.length}</div>
                  <div><span className="font-medium">Positions:</span> {layout.shooting_positions.length}</div>
                </div>
              </div>

              <div className="flex gap-3 flex-col sm:flex-row">
                <button
                  onClick={handleExportJson}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
                >
                  <FileDown className="h-4 w-4" />
                  Export JSON
                </button>

                <button
                  onClick={handleImportJson}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 text-gray-900 text-sm font-medium rounded-md hover:bg-gray-300 transition-colors"
                >
                  <FileDown className="h-4 w-4 rotate-180" />
                  Import JSON
                </button>
              </div>
            </Tabs.Content>
          </Tabs.Root>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ExportDialog;
